import React, { useRef } from 'react';
import {
  Datagrid,
  FunctionField,
  ReferenceField,
  TextField,
  TopToolbar,
  useListContext,
} from 'react-admin';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import PrintIcon from '@material-ui/icons/Print';
import WarningIcon from '@material-ui/icons/Warning';

import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import ListPageComum from '../../Components/ReactAdmin/List/ListPageComum';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { formatMoeda, PRIMARY_COLOR } from '../../utils';
import { PERMISSAO_TELA_CARTAZES_OFERTA_LISTAR } from '../../utils/dadosTelas';
import ContainerDialogoGerarCartazes from './ContainerDialogoGerarCartazes';
import Filtro from './Filtro';

const useStyles = makeStyles(() => ({
  headerCell: {
    backgroundColor: PRIMARY_COLOR,
    color: 'white',
    fontWeight: 'bold',
    // O TableSortLabel do MUI força o texto/seta para preto nos estados
    // hover/focus/ativo, apagando o rótulo sobre o fundo primário — mantém branco.
    '& .MuiTableSortLabel-root': {
      color: 'white',
    },
    '& .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root:focus': {
      color: 'white',
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
      color: 'white',
    },
    '& .MuiTableSortLabel-icon': {
      color: 'white !important',
    },
  },
}));

/** Checkbox de seleção da linha (a tela usa seleção múltipla para gerar em lote). */
const ColunaSelecao = ({ record }) => {
  const { selectedIds = [], onToggleItem } = useListContext();
  if (!record) {
    return null;
  }
  return (
    <Checkbox
      color="primary"
      checked={selectedIds.includes(record.id)}
      onClick={(e) => {
        e.stopPropagation();
        onToggleItem(record.id);
      }}
    />
  );
};

/** Indica se o produto tem `descricao_no_cartaz` — sem ela o cartaz não pode ser gerado. */
const StatusCartazField = ({ record }) => {
  if (!record) {
    return null;
  }
  if (record.descricao_no_cartaz) {
    return (
      <Chip
        size="small"
        label="OK"
        style={{ backgroundColor: '#E8F5E9', color: '#1B5E20' }}
      />
    );
  }
  return (
    <Tooltip title="Produto sem descrição de cartaz — não será possível gerar">
      <Chip
        size="small"
        icon={<WarningIcon style={{ color: '#B71C1C' }} />}
        label="Sem cartaz"
        style={{ backgroundColor: '#FFEBEE', color: '#B71C1C' }}
      />
    </Tooltip>
  );
};

/** Botão de gerar cartazes dos produtos marcados (ação em lote). */
const AcoesTopo = ({ onGerarLote = () => {} }) => {
  const { selectedIds = [], onUnselectItems } = useListContext();

  const gerarLote = () => {
    onGerarLote(selectedIds);
    if (onUnselectItems) {
      onUnselectItems();
    }
  };

  return (
    <TopToolbar>
      <Button
        color="primary"
        variant="contained"
        startIcon={<PrintIcon />}
        disabled={!selectedIds.length}
        onClick={gerarLote}
      >
        {selectedIds.length
          ? `Gerar ${selectedIds.length} cartaz(es)`
          : 'Gerar cartazes'}
      </Button>
    </TopToolbar>
  );
};

const TabelaProdutos = ({ onGerarUm = () => {} }) => {
  const classes = useStyles();
  return (
    <Datagrid
      classes={{ headerCell: classes.headerCell }}
      rowClick={false}
      bulkActionButtons={false}
    >
      <ColunaSelecao label="" sortable={false} />
      <TextField source="codigo" label="Código" />
      <TextField source="nome" label="Produto" />
      <ReferenceField
        source="unidade_id"
        reference="unidades"
        label="Unidade"
        link={false}
        sortable={false}
      >
        <TextField source="nome" />
      </ReferenceField>
      <FunctionField
        label="Preço"
        sortByOrder="ASC"
        sortBy="preco_venda"
        render={(record) =>
          record.preco_oferta != null ? (
            <div>
              <div
                style={{
                  color: 'red',
                  textDecoration: 'line-through',
                  fontSize: '12px',
                }}
              >
                {formatMoeda(record.preco_venda)}
              </div>
              <div style={{ backgroundColor: 'yellow', fontWeight: 'bold' }}>
                {formatMoeda(record.preco_oferta)}
              </div>
            </div>
          ) : (
            formatMoeda(record.preco_venda)
          )
        }
      />
      <StatusCartazField label="Cartaz" sortable={false} />
      <ActionsField
        label="Ações"
        sortable={false}
        actions={[
          {
            key: 'gerar',
            title: 'Gerar cartaz',
            icon: <PrintIcon />,
            onClick: (record) => onGerarUm(record.id),
          },
        ]}
      />
    </Datagrid>
  );
};

const ListaCartazesOferta = () => {
  const refGerar = useRef(null);

  const abrirGeracao = (ids) => {
    if (refGerar.current) {
      refGerar.current.handleAbrir(ids);
    }
  };

  return (
    <ProtegerPagina permissao={PERMISSAO_TELA_CARTAZES_OFERTA_LISTAR}>
      <ListPageComum
        title="Cartazes de Oferta"
        filters={<Filtro />}
        toolbarActions={<AcoesTopo onGerarLote={abrirGeracao} />}
        perPage={25}
        sort={{ field: 'nome', order: 'ASC' }}
        listProps={{
          resource: 'cartazes_oferta',
          basePath: '/cartazes_oferta',
        }}
        renderGrid={() => (
          <>
            <TabelaProdutos onGerarUm={(id) => abrirGeracao([id])} />
            <ContainerDialogoGerarCartazes ref={refGerar} />
          </>
        )}
      />
    </ProtegerPagina>
  );
};

export default ListaCartazesOferta;
